import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../context/User";
import usePushNotifications from "../../hooks/usePushNotifications";
import { isStandalone } from "../../utils/pwa";

// Surfaces automatically the moment someone opens the installed (standalone)
// app while logged in and hasn't decided on notifications yet — no trip to
// Settings required. It can't fire Notification.requestPermission() by
// itself though: iOS Safari only shows that dialog when the call happens
// directly inside a real tap, so this still needs one tap on "Enable" to
// actually open the OS prompt. Re-appears each app open until the user
// either grants or denies at the OS level (dismissing just hides it for
// this visit), which is deliberate — every extra open is another chance to
// convert someone who hasn't said no yet.
export default function AutoPushPrompt() {
  const { isLoggedIn, profile, isLoading } = useContext(UserContext);
  const { supported, permission, loading, enable } = usePushNotifications();
  const [dismissedThisVisit, setDismissedThisVisit] = useState(false);

  const eligible =
    supported &&
    isStandalone() &&
    !isLoading &&
    isLoggedIn &&
    !!profile?._id &&
    permission === "default" &&
    !dismissedThisVisit;

  // Give the page a beat to settle (service worker registering, profile
  // query landing) rather than flashing this in on the very first paint.
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!eligible) {
      setShow(false);
      return;
    }
    const timer = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(timer);
  }, [eligible]);

  if (!show) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        left: { xs: 12, sm: 24 },
        right: { xs: 12, sm: "auto" },
        bottom: { xs: 12, sm: 24 },
        maxWidth: 380,
        p: 2,
        borderRadius: 3,
        zIndex: (t) => t.zIndex.modal + 100,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (t) => t.palette.primary.lightest,
          color: (t) => t.palette.primary.main,
        }}
      >
        <NotificationsActiveRoundedIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Stay updated on your orders
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Turn on notifications to know the moment your order's on its way.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={loading}
          onClick={enable}
          sx={{ mt: 1.5, textTransform: "none" }}
        >
          Enable Notifications
        </Button>
      </Box>
      <IconButton
        size="small"
        aria-label="Not now"
        onClick={() => setDismissedThisVisit(true)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}
